import {
  getRolePermissions,
  replaceRolePermissions,
  RoleCatalogApiError,
  type RoleCatalogItem,
  type RolePermissionAssignment,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  CanonicalDefinitionList,
  CanonicalFilterBar,
  CanonicalModal,
  CanonicalNotice,
  CanonicalStatus,
  FormActions,
  RowSelection,
  SearchField,
  SelectField,
  StatePanel,
  TextAreaField,
  canonicalStates,
} from '@urbanik/ui';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
export function RolePermissionsEditor({
  role,
  onClose,
  canManage,
}: {
  role: RoleCatalogItem | null;
  onClose: () => void;
  canManage: boolean;
}) {
  const [assignment, setAssignment] = useState<RolePermissionAssignment | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!role) return;
    const c = new AbortController();
    setState('loading');
    getRolePermissions(apiBase, role.id, c.signal)
      .then((r) => {
        setAssignment(r.assignment);
        setSelected(new Set(r.assignment.permissions.filter((p) => p.selected).map((p) => p.id)));
        setState('ready');
      })
      .catch((e) => {
        if (!c.signal.aborted) {
          setNotice({
            message: e instanceof Error ? e.message : 'No se pudo cargar la matriz.',
            tone: 'error',
          });
          setState('error');
        }
      });
    return () => c.abort();
  }, [role]);
  const changed = assignment
    ? assignment.permissions.some((p) => p.selected !== selected.has(p.id))
    : false;
  const visible = useMemo(
    () =>
      assignment?.permissions.filter((p) => {
        const q = (p.name + ' ' + p.code + ' ' + p.description).toLowerCase();
        return (
          q.includes(search.toLowerCase()) &&
          (filter === 'all' || (filter === 'selected') === selected.has(p.id))
        );
      }) ?? [],
    [assignment, filter, search, selected],
  );
  const groups = useMemo(() => Map.groupBy(visible, (p) => p.module), [visible]);
  async function save() {
    if (!assignment || !changed || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await replaceRolePermissions(apiBase, assignment.roleId, {
        permissionIds: [...selected],
        expectedVersion: assignment.version,
        reason: reason.trim(),
      });
      setAssignment(r.assignment);
      setSelected(new Set(r.assignment.permissions.filter((p) => p.selected).map((p) => p.id)));
      setReason('');
      setNotice({
        message: 'Permisos del rol actualizados. Las sesiones afectadas fueron revocadas.',
        tone: 'success',
      });
    } catch (e) {
      setNotice({
        message: e instanceof RoleCatalogApiError ? e.message : 'No pudimos guardar los permisos.',
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <CanonicalModal
      open={role !== null}
      title={role ? 'Permisos de ' + role.name : 'Permisos por rol'}
      eyebrow="CR-M02-001.4 · Matriz editable"
      description="Selecciona los accesos que corresponden al rol. La autorización siempre se aplica en el servidor."
      size="large"
      onClose={() => !busy && onClose()}
    >
      <CanonicalNotice
        message={notice?.message ?? ''}
        tone={notice?.tone ?? 'success'}
        onDismiss={() => setNotice(null)}
      />
      {state === 'loading' ? (
        <StatePanel state={canonicalStates.find((s) => s.kind === 'loading')!} />
      ) : null}
      {state === 'error' ? (
        <StatePanel state={canonicalStates.find((s) => s.kind === 'error')!} />
      ) : null}
      {state === 'ready' && assignment ? (
        <div>
          <CanonicalDefinitionList
            label="Resumen del rol"
            items={[
              { label: 'Código', value: assignment.roleCode },
              { label: 'Usuarios afectados', value: assignment.userCount },
              { label: 'Seleccionados', value: selected.size },
              { label: 'Cambios pendientes', value: changed ? 'Sí' : 'No' },
            ]}
          />
          <CanonicalFilterBar>
            <SearchField
              label="Buscar permisos"
              placeholder="Nombre, código o descripción"
              value={search}
              onChange={setSearch}
            />
            <SelectField
              label="Mostrar"
              value={filter}
              onValueChange={setFilter}
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'selected', label: 'Seleccionados' },
                { value: 'unselected', label: 'No seleccionados' },
              ]}
            />
          </CanonicalFilterBar>
          {visible.length === 0 ? (
            <StatePanel state={canonicalStates.find((s) => s.kind === 'empty')!} />
          ) : null}
          {[...groups].map(([module, items]) => (
            <CanonicalCard key={module} title={module.toUpperCase()}>
              {items.map((permission) => (
                <div key={permission.id}>
                  <RowSelection
                    checked={selected.has(permission.id)}
                    disabled={!canManage || busy}
                    label={'Asignar ' + permission.name}
                    onChange={(checked) =>
                      setSelected((current) => {
                        const next = new Set(current);
                        if (checked) next.add(permission.id);
                        else next.delete(permission.id);
                        return next;
                      })
                    }
                  />
                  <strong>{permission.name}</strong>{' '}
                  <CanonicalStatus tone={selected.has(permission.id) ? 'success' : 'neutral'}>
                    {selected.has(permission.id) ? 'Permitido' : 'No permitido'}
                  </CanonicalStatus>
                  <p>{permission.description}</p>
                  <small>{permission.code}</small>
                </div>
              ))}
            </CanonicalCard>
          ))}
          {canManage ? (
            <>
              <TextAreaField
                label="Motivo del cambio"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={3}
                maxLength={500}
                rows={3}
                hint="Explica por qué este rol necesita ganar o perder estos accesos."
              />
              <FormActions>
                <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={busy || !changed || reason.trim().length < 3}
                  onClick={() => void save()}
                >
                  {busy ? 'Guardando…' : 'Guardar permisos'}
                </Button>
              </FormActions>
            </>
          ) : null}
        </div>
      ) : null}
    </CanonicalModal>
  );
}
